import { DateTime, Duration } from 'luxon';

/**
 * Tenant policy for planned work (WBS-11000 / WBS-10921 "Booking And Materialisation Horizons").
 *
 * Two independent knobs that both bound how far the system commits ahead of itself:
 *
 * - The BOOKING HORIZON decides how far forward the sweep persists Visits and mints numbers. Beyond
 *   it, occurrences are computed on demand and cost nothing.
 * - The CREATE JOB LEAD TIME decides how far ahead of its appointment a Visit's Job is created.
 *
 * Neither is a feature flag. Both are ordinary operational policy, so they live in one section of the
 * tenant's General Setting document (`GeneralSetting.plannedWork`) rather than a flag each — read it
 * with `getPlannedWorkSettings`, which applies the per-field fallback below.
 */

/**
 * How many days ahead the sweep persists Visits.
 *
 * A short list of presets rather than a free integer, because the horizon is a cost dial as much as a
 * policy one: every extra day is more persisted Visits, more minted numbers and more reconciliation
 * work on every schedule change. Two weeks is the maximum dispatch planning window; a tenant that
 * needs to book further ahead does it by interacting with the occurrence directly, which persists that
 * one Visit immediately regardless of the horizon.
 */
export enum BookingHorizonDays {
    OneWeek = 7,
    TwoWeeks = 14,
}

/** The presets a tenant may choose from, ascending. The UI renders exactly this list. */
export const BOOKING_HORIZON_CHOICES: readonly BookingHorizonDays[] = [BookingHorizonDays.OneWeek, BookingHorizonDays.TwoWeeks];

/** Bounds automatic visit/card creation, independently of the longer read-only forecast. */
export const MAX_BOOKING_HORIZON_DAYS = BookingHorizonDays.TwoWeeks;
export const DEFAULT_BOOKING_HORIZON_DAYS = MAX_BOOKING_HORIZON_DAYS;

/**
 * How far ahead of the appointment the stamper creates its Job (WBS-11652).
 *
 * Jobs are created at the last practical moment because they are heavy: every one mints a number, books
 * allocations and fans out side effects, and a Job created days early is rewritten by every plan edit
 * in between. Durations are ISO 8601 so the stored value is self-describing; `StartOfDay` is 00:00 on
 * the appointment day in the site timezone, and is the only lead an all-day Visit can use.
 */
export enum CreateJobLeadTime {
    ThirtyMinutes = 'PT30M',
    OneHour = 'PT1H',
    TwoHours = 'PT2H',
    FourHours = 'PT4H',
    StartOfDay = 'StartOfDay',
}

/** The leads a tenant may choose from, in the order the UI renders them. */
export const CREATE_JOB_LEAD_TIME_CHOICES: readonly CreateJobLeadTime[] = [
    CreateJobLeadTime.ThirtyMinutes,
    CreateJobLeadTime.OneHour,
    CreateJobLeadTime.TwoHours,
    CreateJobLeadTime.FourHours,
    CreateJobLeadTime.StartOfDay,
];

/** Two hours covers a morning dispatch review without committing the whole day at midnight. */
export const DEFAULT_CREATE_JOB_LEAD_TIME = CreateJobLeadTime.TwoHours;

export function isValidCreateJobLeadTime(value: unknown): value is CreateJobLeadTime {

    return CREATE_JOB_LEAD_TIME_CHOICES.includes(value as CreateJobLeadTime);
}

/**
 * The instant from which an appointment's Job may be stamped.
 *
 * An all-day Visit has no meaningful start time, so a duration before it would land on the previous
 * evening; it always uses the start of its own local day instead.
 */
export function createJobLeadBoundary(appointmentStart: Date, allDay: boolean, lead: CreateJobLeadTime, timezone: string): Date {

    const start = DateTime.fromJSDate(appointmentStart, { zone: timezone });
    if (allDay || lead === CreateJobLeadTime.StartOfDay) return start.startOf('day').toJSDate();
    return start.minus(Duration.fromISO(lead)).toJSDate();
}

/** Consecutive failed Job creations before an occurrence is quarantined as "Needs attention". */
export const DEFAULT_JOB_CREATION_MAX_ATTEMPTS = 5;
export const MIN_JOB_CREATION_MAX_ATTEMPTS = 1;

/** Beyond twenty, backoff has stretched past the lead time and a person should be looking anyway. */
export const MAX_JOB_CREATION_MAX_ATTEMPTS = 20;

export function isValidJobCreationMaxAttempts(value: unknown): value is number {

    return Number.isInteger(value) && (value as number) >= MIN_JOB_CREATION_MAX_ATTEMPTS && (value as number) <= MAX_JOB_CREATION_MAX_ATTEMPTS;
}

/**
 * Runaway protection for the stamper: the most NEW Jobs it may create in one tenant-local day. Sized
 * well above a 5k/day tenant so it only trips on a bug or a bad bulk edit, never on normal volume.
 */
export const DEFAULT_JOB_CREATION_DAILY_LIMIT = 10_000;

/** Zero is a valid limit: it pauses automatic creation without turning the stamper off. */
export const MIN_JOB_CREATION_DAILY_LIMIT = 0;

export function isValidJobCreationDailyLimit(value: unknown): value is number {

    return Number.isInteger(value) && (value as number) >= MIN_JOB_CREATION_DAILY_LIMIT;
}

/**
 * A bounded stretch of planned work, named relative to today.
 *
 * Planned work is read a period at a time — never "everything forward" — because the forecast walks
 * a twelve-month projection and an unbounded read pays for all of it. So every screen that lists
 * planned work picks a period from this one vocabulary, and the two audiences take the subset that
 * makes sense for them: an approval is always about work that has NOT happened yet, so it offers
 * only the `Next*` half; a Visits list is about what is happening, so it offers both halves.
 */
export enum PlannedWorkPeriodPreset {
    ThisWeek = 'ThisWeek',
    NextWeek = 'NextWeek',
    ThisFortnight = 'ThisFortnight',
    NextFortnight = 'NextFortnight',
    ThisMonth = 'ThisMonth',
    NextMonth = 'NextMonth',
}

/**
 * The approval subset: the period AFTER the one containing today.
 *
 * Customers approve the work that is COMING, not the work in progress: in September the conversation
 * is about October. The arrows still step back to the current period, so choosing a default costs
 * nothing. Declared as its own type so the approval setting cannot be given a `This*` value.
 */
export type VisitApprovalPeriodPreset = PlannedWorkPeriodPreset.NextWeek | PlannedWorkPeriodPreset.NextFortnight | PlannedWorkPeriodPreset.NextMonth;

/** The presets the approval dialog offers, in the order it renders them. */
export const VISIT_APPROVAL_PERIOD_CHOICES: readonly VisitApprovalPeriodPreset[] = [PlannedWorkPeriodPreset.NextWeek, PlannedWorkPeriodPreset.NextFortnight, PlannedWorkPeriodPreset.NextMonth];

/** The presets the Visits list offers, in the order it renders them: current period first. */
export const VISIT_LIST_PERIOD_CHOICES: readonly PlannedWorkPeriodPreset[] = [
    PlannedWorkPeriodPreset.ThisWeek,
    PlannedWorkPeriodPreset.NextWeek,
    PlannedWorkPeriodPreset.ThisFortnight,
    PlannedWorkPeriodPreset.NextFortnight,
    PlannedWorkPeriodPreset.ThisMonth,
    PlannedWorkPeriodPreset.NextMonth,
];

/** A whole calendar month is how most service contracts are billed and discussed. */
export const DEFAULT_VISIT_APPROVAL_PERIOD: VisitApprovalPeriodPreset = PlannedWorkPeriodPreset.NextMonth;

/**
 * The Visits list opens on the month in progress.
 *
 * Not the twelve-month forecast it used to open on: that read was the whole projection, and the
 * question a planner actually arrives with is "what is happening now", not "what is ever scheduled".
 */
export const DEFAULT_VISIT_LIST_PERIOD = PlannedWorkPeriodPreset.ThisMonth;

/** A week covers a missed fortnightly slot being caught up without it vanishing into Missed first. */
export const DEFAULT_OVERDUE_GRACE_DAYS = 7;

/**
 * Past-Visit reads stop at 35 days (`VISIT_PAST_RETENTION_DAYS`), so capping grace at 30 guarantees
 * every Visit spends at least five days as Missed and is counted in missed reporting before it is gone.
 */
export const MAX_OVERDUE_GRACE_DAYS = 30;

/** How long a past Visit row is kept (plan section 9); the rows are the attendance history, so this bounds it. */
export const DEFAULT_VISIT_HISTORY_RETENTION_MONTHS = 13;
export const MIN_VISIT_HISTORY_RETENTION_MONTHS = 1;
export const MAX_VISIT_HISTORY_RETENTION_MONTHS = 120;

export function isValidVisitHistoryRetentionMonths(value: unknown): value is number {

    return Number.isInteger(value) && (value as number) >= MIN_VISIT_HISTORY_RETENTION_MONTHS && (value as number) <= MAX_VISIT_HISTORY_RETENTION_MONTHS;
}

/** Opt-in: a digest nobody asked for trains owners to ignore notifications. */
export const DEFAULT_OVERDUE_OWNER_DIGEST = false;

export interface PlannedWorkSettings {
    bookingHorizonDays: BookingHorizonDays;
    createJobBefore: CreateJobLeadTime;
    /** Consecutive failures before the stamper stops retrying an occurrence and asks for a person. */
    jobCreationMaxAttempts: number;
    /** New Jobs the stamper may create per tenant-local day; 0 pauses automatic creation. */
    jobCreationDailyLimit: number;
    visitApprovalDefaultPeriod: VisitApprovalPeriodPreset;
    visitListDefaultPeriod: PlannedWorkPeriodPreset;
    /** Days a passed, job-less Visit stays Overdue before reading as Missed; a Service Line may override it. */
    overdueGraceDays: number;
    /** One daily digest per Service Line owner listing their overdue Visits. */
    overdueOwnerDigest: boolean;
    /** Months a past Visit is kept: after its Job completes or is cancelled, or after its date when it has no Job. */
    visitHistoryRetentionMonths: number;
}

export const DEFAULT_PLANNED_WORK_SETTINGS: PlannedWorkSettings = {
    bookingHorizonDays: DEFAULT_BOOKING_HORIZON_DAYS,
    createJobBefore: DEFAULT_CREATE_JOB_LEAD_TIME,
    jobCreationMaxAttempts: DEFAULT_JOB_CREATION_MAX_ATTEMPTS,
    jobCreationDailyLimit: DEFAULT_JOB_CREATION_DAILY_LIMIT,
    visitApprovalDefaultPeriod: DEFAULT_VISIT_APPROVAL_PERIOD,
    visitListDefaultPeriod: DEFAULT_VISIT_LIST_PERIOD,
    overdueGraceDays: DEFAULT_OVERDUE_GRACE_DAYS,
    overdueOwnerDigest: DEFAULT_OVERDUE_OWNER_DIGEST,
    visitHistoryRetentionMonths: DEFAULT_VISIT_HISTORY_RETENTION_MONTHS,
};

export function isValidBookingHorizon(value: unknown): value is BookingHorizonDays {

    return BOOKING_HORIZON_CHOICES.includes(value as BookingHorizonDays);
}

export function isValidVisitApprovalPeriod(value: unknown): value is VisitApprovalPeriodPreset {

    return VISIT_APPROVAL_PERIOD_CHOICES.includes(value as VisitApprovalPeriodPreset);
}

export function isValidPlannedWorkPeriod(value: unknown): value is PlannedWorkPeriodPreset {

    return Object.values(PlannedWorkPeriodPreset).includes(value as PlannedWorkPeriodPreset);
}

/** Whole days only: a fractional grace would make Overdue flip mid-day in one timezone and not another. */
export function isValidOverdueGraceDays(value: unknown): value is number {

    return Number.isInteger(value) && (value as number) >= 0 && (value as number) <= MAX_OVERDUE_GRACE_DAYS;
}

/**
 * Coerce whatever is stored into a usable policy, falling back per FIELD rather than per document.
 *
 * A tenant setting is a `Mixed` blob that predates the shape it now holds, so a partially-written or
 * hand-edited value is normal rather than exceptional. Falling back field by field means one bad value
 * cannot discard a good one beside it, and — because the sweep reads this on every run — a nonsense
 * horizon degrades to the default rather than persisting nothing or persisting for ever.
 */
export function normalizePlannedWorkSettings(settings?: Partial<PlannedWorkSettings> | null): PlannedWorkSettings {

    const bookingHorizonDays = isValidBookingHorizon(settings?.bookingHorizonDays) ? settings!.bookingHorizonDays! : DEFAULT_BOOKING_HORIZON_DAYS;
    const createJobBefore = isValidCreateJobLeadTime(settings?.createJobBefore) ? settings!.createJobBefore! : DEFAULT_CREATE_JOB_LEAD_TIME;
    const jobCreationMaxAttempts = isValidJobCreationMaxAttempts(settings?.jobCreationMaxAttempts) ? settings!.jobCreationMaxAttempts! : DEFAULT_JOB_CREATION_MAX_ATTEMPTS;
    const jobCreationDailyLimit = isValidJobCreationDailyLimit(settings?.jobCreationDailyLimit) ? settings!.jobCreationDailyLimit! : DEFAULT_JOB_CREATION_DAILY_LIMIT;
    const visitApprovalDefaultPeriod = isValidVisitApprovalPeriod(settings?.visitApprovalDefaultPeriod) ? settings!.visitApprovalDefaultPeriod! : DEFAULT_VISIT_APPROVAL_PERIOD;
    const visitListDefaultPeriod = isValidPlannedWorkPeriod(settings?.visitListDefaultPeriod) ? settings!.visitListDefaultPeriod! : DEFAULT_VISIT_LIST_PERIOD;
    const overdueGraceDays = isValidOverdueGraceDays(settings?.overdueGraceDays) ? settings!.overdueGraceDays! : DEFAULT_OVERDUE_GRACE_DAYS;
    const overdueOwnerDigest = typeof settings?.overdueOwnerDigest === 'boolean' ? settings.overdueOwnerDigest : DEFAULT_OVERDUE_OWNER_DIGEST;
    const visitHistoryRetentionMonths = isValidVisitHistoryRetentionMonths(settings?.visitHistoryRetentionMonths) ? settings!.visitHistoryRetentionMonths! : DEFAULT_VISIT_HISTORY_RETENTION_MONTHS;

    return { bookingHorizonDays, createJobBefore, jobCreationMaxAttempts, jobCreationDailyLimit, visitApprovalDefaultPeriod, visitListDefaultPeriod, overdueGraceDays, overdueOwnerDigest, visitHistoryRetentionMonths };
}
